import type { ReactNode } from "react";
import { StatusDropdown } from "admin/components/StatusDropdown";
import { ScoreBar, sortDimensionScores } from "admin/components/diagnostics/ScoreBar";
import { SeverityPill } from "admin/components/diagnostics/SeverityPill";
import { useTriageStatus } from "admin/hooks/useTriageStatus";
import {
  TRIAGE_STATUS_OPTIONS,
  getAgentLabel,
  getTriageStatusConfig,
} from "admin/types/constants/diagnostics";
import type {
  DiagnosticListItem,
  DiagnosticTriageStatus,
  RubricDimension,
} from "admin/types/interfaces/diagnostics";
import { scoreColor } from "admin/utils/scoring";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { turnAnchorId } from "./useAnchoredScroll";

export interface TurnDiagnosticsRailProps {
  diagnostic: DiagnosticListItem;
  /** 1-based position of this turn in the transcript. */
  ordinal: number;
  totalTurns: number;
  onClose: () => void;
  onPrevFlagged: () => void;
  onNextFlagged: () => void;
  hasPrevFlagged: boolean;
  hasNextFlagged: boolean;
}

/**
 * Rubric display names and sort order come from the diagnostics `/summary`
 * endpoint, which this surface does not fetch, so dimensions keep their stored
 * order. Sorting through the shared helper anyway still sinks the unscored
 * (`null`) dimensions to the bottom, which is the ordering that carries meaning.
 */
const RUBRIC_UNAVAILABLE: RubricDimension[] = [];

// `excluded` is not a verdict an operator sets from here: it is how a turn gets
// left out of the run in the first place.
const TURN_REVIEW_STATUS_OPTIONS = TRIAGE_STATUS_OPTIONS.filter((opt) => opt.value !== "excluded");

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </h3>
);

/**
 * Detail for one scored turn, shown beside the transcript rather than over it so
 * an operator can read the turn and its diagnosis at the same time.
 */
export const TurnDiagnosticsRail = ({
  diagnostic,
  ordinal,
  totalTurns,
  onClose,
  onPrevFlagged,
  onNextFlagged,
  hasPrevFlagged,
  hasNextFlagged,
}: TurnDiagnosticsRailProps) => {
  const { updateTriageStatus, updatingIds } = useTriageStatus();

  const statusConfig = getTriageStatusConfig(diagnostic.triageStatus);
  const dimensionScores = sortDimensionScores(diagnostic.dimensionScores ?? [], RUBRIC_UNAVAILABLE);
  // The stored turn id is a full X-Ray header; its root trace id is what
  // identifies the turn everywhere else (transcript anchors, deep links).
  const traceId = turnAnchorId(diagnostic.turnId) ?? diagnostic.turnId;

  const handleTriageChange = (status: DiagnosticTriageStatus) =>
    updateTriageStatus(diagnostic.id, status);

  return (
    <aside
      aria-label="Turn diagnostics"
      // `min-w-[363px]` is the panel column's content width (its 364px less its
      // left border). Pinning it lets the column clip this panel as it slides
      // instead of re-wrapping every line of it at each intermediate width.
      className="flex min-h-0 min-w-[363px] flex-1 flex-col bg-card"
    >
      <div className="flex shrink-0 flex-col gap-2 border-b border-border px-4 pt-3.5 pb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">Turn Detail</h2>
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">
            Turn {ordinal} of {totalTurns}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            onClick={onClose}
            aria-label="Close turn diagnostics"
          >
            <X className="size-3.5" />
          </Button>
        </div>
        <div
          className="truncate font-mono text-[10px] text-muted-foreground"
          title={diagnostic.turnId}
        >
          {traceId}
        </div>
        <div className="flex items-center gap-2">
          <SeverityPill severity={diagnostic.severity} />
          <span
            className={cn(
              "text-lg font-semibold tabular-nums",
              scoreColor(diagnostic.averageScore)
            )}
          >
            {diagnostic.averageScore != null ? diagnostic.averageScore.toFixed(2) : "N/A"}
          </span>
          {diagnostic.averageScore != null && (
            <span className="text-xs text-muted-foreground">/ 5</span>
          )}
        </div>
      </div>

      {/* Stacked above the notes, this body is far shorter than its content, and
          the footer's border right below the clip reads as the end of the panel.
          It needs a resting cue, and the scrollbar cannot be it - platforms that
          overlay theirs paint nothing at rest and no CSS overrides that. Same fade
          the assistant transcript uses; the second mask layer keeps the right 16px
          opaque for classic scrollbars, which here is only the `px-4` padding. */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-4 py-3.5",
          "[mask-image:linear-gradient(black_calc(100%-24px),transparent_100%),linear-gradient(black,black)]",
          "[mask-size:calc(100%-16px)_100%,16px_100%]",
          "[mask-position:0px_0px,100%_0px]",
          "[mask-repeat:no-repeat,no-repeat]"
        )}
      >
        <div className="flex flex-col gap-1">
          <FieldLabel>Failure Layer</FieldLabel>
          <p className="text-xs">{getAgentLabel(diagnostic.primaryFailureAgent)}</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel>Turn Review Status</FieldLabel>
          {diagnostic.triageStatus === "excluded" ? (
            <span
              className={cn(
                "inline-flex w-fit items-center rounded-md border px-2 py-1 text-xs",
                statusConfig.badgeClass
              )}
            >
              {statusConfig.label}
            </span>
          ) : (
            // N.B. keyed by the turn: stepping through flagged turns re-renders
            // this rail rather than remounting it, so without the key a write
            // still out for the previous turn leaves this picker inert.
            <StatusDropdown
              key={diagnostic.id}
              value={diagnostic.triageStatus}
              options={TURN_REVIEW_STATUS_OPTIONS}
              onChange={handleTriageChange}
              ariaLabel="Update turn review status"
              errorMessage="Failed to update turn review status"
              disabled={updatingIds.has(diagnostic.id)}
            />
          )}
        </div>

        {diagnostic.failureSummary && (
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Failure Summary</FieldLabel>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {diagnostic.failureSummary}
            </p>
          </div>
        )}

        {dimensionScores.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Dimension Scores</FieldLabel>
            <div className="flex flex-col gap-1.5">
              {dimensionScores.map((ds) => (
                <ScoreBar key={ds.dimension} score={ds.score} label={ds.dimension} compact />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-4 py-2.5">
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={onPrevFlagged}
          disabled={!hasPrevFlagged}
        >
          <ChevronLeft className="size-3" />
          Prev flagged
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          onClick={onNextFlagged}
          disabled={!hasNextFlagged}
        >
          Next flagged
          <ChevronRight className="size-3" />
        </Button>
      </div>
    </aside>
  );
};
